import React from 'react';
import {
  AccessTime,
  Psychology,
  SelfImprovement,
  DirectionsRun,
  Bedtime,
  WaterDrop,
  Air,
  Healing,
  FitnessCenter,
  Nature,
  Schedule,
  TrendingUp,
  Grass,
  SetMeal,
  Science,
  OpenWith,
  LocalFlorist,
  EmojiObjects,
  Spa,
} from '@mui/icons-material';

export interface TalentNode {
  id: string;
  name: string;
  description: string;
  maxSession: number;
  currentSession: number;
  icon: React.ReactNode;
  row: number;
  col: number;
  requires?: string[] | string[][]; // Can be array of strings (AND) or array of arrays (OR groups)
  unlocked: boolean;
  timeRequired: number; // Minutes required per session per week
  ranking: number; // 1-23 ranking for recommendations
  category: 'longevity' | 'healthspan' | 'wellness';
  color: string; // Category color for visual consistency
}

// Talent ranking as specified by user
export const TALENT_RANKINGS = {
  'health-tree': 0, // Special root node
  'sleep-hygiene': 1,
  'oral-health': 2,
  'strength-training': 3,
  'zone-two-cardio': 4,
  'zone-five-cardio': 5,
  'sunseeker': 6,
  'creatine-supplementation': 7,
  'energy-balance': 8,
  'time-restricted-eating': 9,
  'fiber-intake': 10,
  'protein-optimization': 11,
  // Random rankings for remaining talents (13-19)
  'stability-mastery': 13,
  'functional-mobility': 14,
  'guided-breathwork': 15,
  'social-connection': 16,
  'emotional-regulation': 17,
  'recovery-mastery': 18,
  'elastic-edge': 19,
  'purpose-meaning': 20,
};

// Health Tree structure - flows from bottom to top like a real tree
export const UNIFIED_TALENT_TREE: TalentNode[] = [
  // ROOT NODE - Health Tree (bottom of screen, no time requirement)
  {
    id: 'health-tree',
    name: 'Health Tree',
    description: 'The foundation of your health journey. Your personal health tree grows as you develop healthy habits. This is automatically planted for you!',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(Nature),
    row: 0, // Bottom row - root of the tree
    col: 10, // Center column
    unlocked: true, // Always unlocked as foundation
    timeRequired: 0, // No time requirement - just the tree format
    ranking: 0, // Special ranking for root
    category: 'longevity',
    color: '#8b4513' // Brown color for tree trunk
  },
  
  // FIRST BRANCH LEVEL - Sleep Hygiene (grows from Health Tree)
  {
    id: 'sleep-hygiene',
    name: 'Sleep Hygiene',
    description: 'Master the foundation of health with optimal sleep practices. Quality sleep affects every aspect of longevity.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(Bedtime),
    row: 2, // Higher up from root
    col: 10, // Center column
    requires: ['health-tree'], // Grows from Health Tree
    unlocked: false,
    timeRequired: 15,
    ranking: TALENT_RANKINGS['sleep-hygiene'],
    category: 'longevity',
    color: '#9c27b0'
  },
  {
    id: 'zone-two-cardio',
    name: 'Zone Two Cardio',
    description: 'Build mitochondrial capacity with steady-state cardio. The cornerstone of metabolic health. 3 sessions of 60 minutes per week.',
    maxSession: 3,
    currentSession: 0,
    icon: React.createElement(DirectionsRun),
    row: 4, // Higher up the tree
    col: 8, // Swapped with oral-health (now on left)
    requires: ['sleep-hygiene'],
    unlocked: false,
    timeRequired: 60, // 60 minutes per session
    ranking: TALENT_RANKINGS['zone-two-cardio'],
    category: 'longevity',
    color: '#9c27b0'
  },
  {
    id: 'oral-health',
    name: 'Oral Health',
    description: 'Prevent systemic inflammation through dental hygiene. Connected to heart health and longevity.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(Healing),
    row: 4, // Same level
    col: 10, // Swapped with zone-two-cardio (now in center)
    requires: ['sleep-hygiene'],
    unlocked: false,
    timeRequired: 25,
    ranking: TALENT_RANKINGS['oral-health'],
    category: 'longevity',
    color: '#9c27b0'
  },
  {
    id: 'strength-training',
    name: 'Strength Training',
    description: '10-minute zone 2 cardio warm-up per session, 50 minutes per session strength training. Essential for maintaining independence and metabolic health.',
    maxSession: 3,
    currentSession: 0,
    icon: React.createElement(FitnessCenter),
    row: 4, // Same level as zone-two-cardio
    col: 12, // Moved to right where oral-health was (was col 8)
    requires: ['sleep-hygiene'],
    unlocked: false,
    timeRequired: 60,
    ranking: TALENT_RANKINGS['strength-training'],
    category: 'longevity',
    color: '#9c27b0'
  },
  {
    id: 'zone-five-cardio',
    name: 'Zone Five Cardio',
    description: 'High-intensity training for VO2 max improvement. Requires solid Zone 2 base.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(TrendingUp),
    row: 6, // Higher tier
    col: 8, // Swapped with energy-balance (follows zone-two-cardio on left)
    requires: ['zone-two-cardio'],
    unlocked: false,
    timeRequired: 30,
    ranking: TALENT_RANKINGS['zone-five-cardio'],
    category: 'longevity',
    color: '#9c27b0'
  },
  {
    id: 'protein-optimization',
    name: 'Protein Optimization',
    description: 'Maximize muscle protein synthesis. Essential for maintaining lean mass with age.',
    maxSession: 2,
    currentSession: 0,
    icon: React.createElement(SetMeal),
    row: 6, // Same tier
    col: 12, // Follow strength-training to the right
    requires: ['strength-training'],
    unlocked: false,
    timeRequired: 30,
    ranking: TALENT_RANKINGS['protein-optimization'],
    category: 'longevity',
    color: '#9c27b0'
  },
  {
    id: 'energy-balance',
    name: 'Energy Balance',
    description: 'Master caloric intake vs expenditure. Foundation for all nutritional optimization.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(TrendingUp),
    row: 6, // Same tier
    col: 10, // Swapped with zone-five-cardio (follows oral-health in center)
    requires: ['oral-health'],
    unlocked: false,
    timeRequired: 45,
    ranking: TALENT_RANKINGS['energy-balance'],
    category: 'longevity',
    color: '#9c27b0'
  },
  {
    id: 'time-restricted-eating',
    name: 'Time Restricted Eating',
    description: 'Optimize metabolic health through meal timing. Simple yet powerful intervention.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(Schedule),
    row: 8, // Higher tier
    col: 10, // Directly under energy-balance
    requires: ['energy-balance'],
    unlocked: false,
    timeRequired: 25,
    ranking: TALENT_RANKINGS['time-restricted-eating'],
    category: 'longevity',
    color: '#9c27b0'
  },
  {
    id: 'fiber-intake',
    name: 'Fiber Intake',
    description: 'Support gut health and satiety. Critical for metabolic health and longevity.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(Grass),
    row: 8, // Same tier
    col: 8, // Directly under zone-five-cardio
    requires: [['zone-five-cardio'], ['energy-balance']], // OR condition: either zone-five-cardio OR energy-balance
    unlocked: false,
    timeRequired: 30,
    ranking: TALENT_RANKINGS['fiber-intake'],
    category: 'longevity',
    color: '#9c27b0'
  },
  {
    id: 'creatine-supplementation',
    name: 'Creatine Supplementation',
    description: 'Enhance muscle performance and brain function. Proven supplement for strength athletes.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(Science),
    row: 8, // Same tier
    col: 12, // Directly under protein-optimization
    requires: [['energy-balance'], ['protein-optimization']], // OR condition: either energy-balance OR protein-optimization
    unlocked: false,
    timeRequired: 10,
    ranking: TALENT_RANKINGS['creatine-supplementation'],
    category: 'longevity',
    color: '#9c27b0'
  },

  // HEALTHSPAN TALENTS (LEFT BRANCH) - Cyan theme
  {
    id: 'stability-mastery',
    name: 'Stability Mastery',
    description: 'Build the foundation for movement quality. Prevents injury and enhances all physical activities. 6 sessions of 10 minutes each.',
    maxSession: 6,
    currentSession: 0,
    icon: React.createElement(OpenWith),
    row: 2, // Same level as sleep hygiene
    col: 6, // Closer to center, left branch of tree
    requires: ['health-tree'], // Connected to Health Tree root
    unlocked: false,
    timeRequired: 10,
    ranking: TALENT_RANKINGS['stability-mastery'],
    category: 'healthspan',
    color: '#00bcd4'
  },
  {
    id: 'functional-mobility',
    name: 'Functional Mobility',
    description: 'Maintain range of motion for daily activities. Foundation for lifelong movement.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(SelfImprovement),
    row: 4, // Same level as strength training
    col: 5, // Closer to center, under stability mastery
    requires: ['stability-mastery'],
    unlocked: false,
    timeRequired: 60,
    ranking: TALENT_RANKINGS['functional-mobility'],
    category: 'healthspan',
    color: '#00bcd4'
  },
  {
    id: 'elastic-edge',
    name: 'Elastic Edge',
    description: 'Advanced movement patterns for athletic performance. Builds on stability foundation.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(LocalFlorist),
    row: 6, // Higher than stability mastery
    col: 6, // Directly under stability mastery
    requires: ['stability-mastery'], // Connected to Stability Mastery instead
    unlocked: false,
    timeRequired: 30,
    ranking: TALENT_RANKINGS['elastic-edge'],
    category: 'healthspan',
    color: '#00bcd4'
  },
  {
    id: 'recovery-mastery',
    name: 'Recovery Mastery',
    description: 'Optimize recovery for peak performance. Advanced recovery protocols.',
    maxSession: 1,
    currentSession: 0,
    icon: React.createElement(Spa),
    row: 4, // Same level as functional mobility
    col: 7, // To the right of stability mastery
    requires: ['stability-mastery'], // Connected to stability mastery foundation
    unlocked: false,
    timeRequired: 30,
    ranking: TALENT_RANKINGS['recovery-mastery'],
    category: 'healthspan',
    color: '#00bcd4'
  },

  // WELLNESS TALENTS (RIGHT BRANCH) - Green theme
  {
    id: 'sunseeker',
    name: 'Morning Sun Seeker',
    description: 'Optimize circadian rhythm with morning light exposure. Foundation for mental wellness.',
    maxSession: 5,
    currentSession: 0,
    icon: React.createElement(WaterDrop),
    row: 2, // Same level as sleep hygiene and stability mastery
    col: 14, // Closer to center, right branch of tree
    requires: ['health-tree'], // Connected to Health Tree root
    unlocked: false,
    timeRequired: 10,
    ranking: TALENT_RANKINGS['sunseeker'],
    category: 'wellness',
    color: '#4caf50'
  },
  {
    id: 'guided-breathwork',
    name: 'Guided Breath Work',
    description: 'Master breathing techniques for stress management and performance.',
    maxSession: 5,
    currentSession: 0,
    icon: React.createElement(Air),
    row: 4, // Mid level
    col: 15, // Moved left from 16
    requires: ['sunseeker'],
    unlocked: false,
    timeRequired: 5,
    ranking: TALENT_RANKINGS['guided-breathwork'],
    category: 'wellness',
    color: '#4caf50'
  },
  {
    id: 'social-connection',
    name: 'Social Connection',
    description: 'Build and maintain meaningful relationships. Critical for mental health and longevity.',
    maxSession: 3,
    currentSession: 0,
    icon: React.createElement(Psychology),
    row: 4, // Mid level
    col: 13, // Moved right to avoid overlap with Strength Training
    requires: ['sunseeker'],
    unlocked: false,
    timeRequired: 30,
    ranking: TALENT_RANKINGS['social-connection'],
    category: 'wellness',
    color: '#4caf50'
  },
  {
    id: 'purpose-meaning',
    name: 'Purpose & Meaning',
    description: 'Develop sense of purpose and meaning in life. Foundation for psychological wellness.',
    maxSession: 3,
    currentSession: 0,
    icon: React.createElement(EmojiObjects),
    row: 6, // Higher level
    col: 13.5, // Positioned between col 13 and 14
    requires: ['sunseeker'],
    unlocked: false,
    timeRequired: 10,
    ranking: TALENT_RANKINGS['purpose-meaning'],
    category: 'wellness',
    color: '#4caf50'
  },
  {
    id: 'emotional-regulation',
    name: 'Emotional Regulation',
    description: 'Develop emotional intelligence and stress resilience.',
    maxSession: 2,
    currentSession: 0,
    icon: React.createElement(Psychology),
    row: 6, // Same level as Purpose & Meaning
    col: 15, // To the right of Purpose & Meaning (which is at col 14)
    requires: ['sunseeker'],
    unlocked: false,
    timeRequired: 10,
    ranking: TALENT_RANKINGS['emotional-regulation'],
    category: 'wellness',
    color: '#4caf50'
  }
];

// Time per session lookup for easy access
export const TALENT_TIME_PER_SESSION: Record<string, number> = UNIFIED_TALENT_TREE.reduce(
  (acc, talent) => ({
    ...acc,
    [talent.id]: talent.timeRequired
  }),
  {}
);

// Tree metadata for UI
export const TREE_CATEGORIES = {
  longevity: {
    name: 'Longevity',
    description: 'Length of Life - Extend your years through proven practices',
    color: '#9c27b0',
    icon: React.createElement(AccessTime)
  },
  healthspan: {
    name: 'Health Span',
    description: 'Quality of Life - Maximize vitality and function',
    color: '#00bcd4',
    icon: React.createElement(FitnessCenter)
  },
  wellness: {
    name: 'Wellness',
    description: 'Will to Live - Cultivate purpose and joy',
    color: '#4caf50',
    icon: React.createElement(SelfImprovement)
  }
};

// Get talents by ranking for recommendations
export function getTalentsByRanking(availableMinutes: number = Infinity): TalentNode[] {
  return UNIFIED_TALENT_TREE
    .sort((a, b) => a.ranking - b.ranking)
    .filter((talent, index, arr) => {
      // Calculate cumulative time if we select up to this talent
      const cumulativeTime = arr.slice(0, index + 1)
        .reduce((sum, t) => sum + (t.timeRequired * Math.min(t.maxSession, 1)), 0);
      return cumulativeTime <= availableMinutes;
    });
}

// Get talent by ID
export function getTalentById(id: string): TalentNode | undefined {
  return UNIFIED_TALENT_TREE.find(talent => talent.id === id);
}

// Check if talent is unlocked based on prerequisites (with point investment requirement)
export function isTalentUnlocked(talentId: string, talentsWithPoints: Set<string>): boolean {
  const talent = getTalentById(talentId);
  if (!talent) return false;
  
  // Always unlocked talents (starting talents like health-tree)
  if (talent.unlocked && talent.id === 'health-tree') return true;
  
  // Check if all prerequisites have points invested
  if (!talent.requires || talent.requires.length === 0) return talent.unlocked;
  
  // Handle both simple array (AND) and array of arrays (OR groups)
  if (Array.isArray(talent.requires[0])) {
    // OR condition: at least one group must be satisfied
    return (talent.requires as string[][]).some(group => 
      group.every(reqId => {
        // All prerequisites need points invested
        return talentsWithPoints.has(reqId);
      })
    );
  } else {
    // AND condition: all requirements must be satisfied
    return (talent.requires as string[]).every(reqId => {
      // All prerequisites need points invested
      return talentsWithPoints.has(reqId);
    });
  }
}